import React from 'react';
import { Plus, Trash2, Check } from 'lucide-react';
import { useForm } from '../../context/FormContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { MAX_ADDITIONAL_INCOMES, amountOf } from '../../lib/additionalIncome';

const IncomeRow = ({ row, index, onChange, onRemove }) => {
  const hasAmount = amountOf(row) > 0;
  const hasDesc = !!(row.description || '').trim();
  const descMissing = hasAmount && !hasDesc;
  const amountMissing = hasDesc && !hasAmount;
  const errCls = 'border-red-500 bg-red-50 focus-visible:ring-red-500';
  return (
  <div data-testid={`additional-income-row-${index}`}>
  <div className="flex flex-col sm:flex-row gap-2 sm:items-start p-3 sm:p-0 border sm:border-0 border-slate-200 rounded-lg">
    <div className="flex gap-2 sm:contents">
    <div className="relative flex-1 sm:w-32 sm:flex-none sm:order-1">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
      <Input
        placeholder="per month"
        inputMode="decimal"
        value={row.monthly_amount || ''}
        onChange={(e) => onChange({ monthly_amount: e.target.value.replace(/[^\d.]/g, '') })}
        className={`h-11 pl-6 ${amountMissing ? errCls : ''}`}
        data-testid={`additional-income-amount-${index}`}
      />
    </div>
    <button type="button" onClick={onRemove} className="h-11 px-2 text-slate-400 hover:text-red-600 sm:order-3" aria-label="Remove income" data-testid={`additional-income-remove-${index}`}>
      <Trash2 className="w-4 h-4" />
    </button>
    </div>
    <Input
      placeholder="Where from? e.g. Universal Credit, rent from 2nd property"
      value={row.description || ''}
      onChange={(e) => onChange({ description: e.target.value })}
      className={`h-11 sm:flex-1 sm:order-2 ${descMissing ? errCls : ''}`}
      data-testid={`additional-income-description-${index}`}
    />
  </div>
  {(descMissing || amountMissing) && (
    <p className="text-xs text-red-600 mt-1" data-testid={`additional-income-row-error-${index}`}>
      {descMissing ? 'Please say where this income comes from' : 'Please enter the monthly amount'}
    </p>
  )}
  </div>
  );
};

export const AdditionalIncomeSection = () => {
  const { formData, updateFormData } = useForm();
  const rows = formData.additional_incomes || [];
  const answer = formData.has_additional_income;

  const setAnswer = (val) => updateFormData({
    has_additional_income: val,
    additional_incomes: val === 'yes' && rows.length === 0 ? [{ description: '', monthly_amount: '' }] : rows,
  });
  const updateRow = (i, patch) => updateFormData({ additional_incomes: rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const removeRow = (i) => {
    const next = rows.filter((_, idx) => idx !== i);
    updateFormData({ additional_incomes: next, ...(next.length === 0 ? { has_additional_income: 'no' } : {}) });
  };

  return (
    <div className="border-t border-slate-200 pt-6" data-testid="additional-income-section">
      <Label className="text-sm font-medium text-slate-700 mb-1 block">Do you have any other income?</Label>
      <p className="text-xs text-slate-500 mb-3">e.g. benefits, rent from a 2nd property, pension, maintenance or a second job. Don&apos;t include your salary above.</p>
      <div className="grid grid-cols-2 gap-3">
        {['yes', 'no'].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setAnswer(v)}
            className={`h-11 rounded-lg border-2 font-medium transition-colors flex items-center justify-center gap-1.5 ${answer === v ? 'brand-bg brand-border text-white' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
            data-testid={`additional-income-${v}-btn`}
          >
            {answer === v && <Check className="w-4 h-4" />}
            {v === 'yes' ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
      {answer === 'yes' && (
        <div className="mt-4 space-y-3">
          {rows.map((row, i) => (
            <IncomeRow key={i} row={row} index={i} onChange={(p) => updateRow(i, p)} onRemove={() => removeRow(i)} />
          ))}
          {rows.length < MAX_ADDITIONAL_INCOMES && (
            <Button type="button" variant="outline" onClick={() => updateFormData({ additional_incomes: [...rows, { description: '', monthly_amount: '' }] })} className="w-full h-10" data-testid="additional-income-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Add another income
            </Button>
          )}
          <p className="text-xs text-slate-500">Monthly amounts. For weekly payments, multiply by 52 and divide by 12.</p>
        </div>
      )}
    </div>
  );
};

export default AdditionalIncomeSection;
